import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth.context';
import { accountService, type UserSubscription } from '@/services/account.service';
import { avaliarBloqueio, type SubscriptionStatus } from './use-subscription-status';
import { parseSubscriptionPollingConfig } from '@/lib/subscription-polling-config';
import { subscriptionQueryKeys } from '@/lib/subscription-query-keys';

const BACKOFF_FACTOR = 2;

function awaitingPayment(
  subscription: UserSubscription | null,
  blockReason: SubscriptionStatus['blockReason'],
): boolean {
  const status = subscription?.status?.toLowerCase();
  if (!subscription || status === 'cancelled' || status === 'inactive') return false;
  return blockReason === 'payment_overdue' || Boolean(subscription.pending_plan_id);
}

/**
 * Montado uma vez no Layout. Reconcilia somente quem aguarda pagamento,
 * compartilhando o resultado com os guards e avisos já existentes.
 */
export function useSubscriptionPaymentSync(status: SubscriptionStatus): void {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const waiting = awaitingPayment(status.subscription, status.blockReason);
  const [pendingProfileUserId, setPendingProfileUserId] = useState<string | null>(null);
  const latest = useRef({ refreshProfile, terminalSubscription: user?.terminal_subscription });
  latest.current = { refreshProfile, terminalSubscription: user?.terminal_subscription };

  // Se outro consumidor revalidar a query primeiro, ainda precisamos atualizar
  // o perfil que fornece permissões/features antes de encerrar a reconciliação.
  useEffect(() => {
    if (!userId) setPendingProfileUserId(null);
    else if (waiting) setPendingProfileUserId(userId);
  }, [userId, waiting]);

  const enabled = Boolean(userId && (waiting || pendingProfileUserId === userId));
  const polling = enabled ? parseSubscriptionPollingConfig(
    import.meta.env.VITE_SUBSCRIPTION_POLL_INTERVAL_MS,
    import.meta.env.VITE_SUBSCRIPTION_POLL_MAX_INTERVAL_MS,
  ) : null;
  const interval = polling?.intervalMs ?? null;
  const maxInterval = polling?.maxIntervalMs ?? null;
  const cache = queryClient.getQueryCache();
  const subscriptionQuery = cache.find({ queryKey: subscriptionQueryKeys.subscription });

  useEffect(() => {
    if (!enabled || interval === null || maxInterval === null || !subscriptionQuery) return;
    let cancelled = false;
    let inFlight = false;
    let nextInterval = interval;
    let timer: number;
    // clear()/removeQueries() invalidam a identidade sincronicamente, antes
    // mesmo do cleanup React. Uma resposta antiga não recria dados no cache.
    const currentSession = () => !cancelled &&
      cache.find({ queryKey: subscriptionQueryKeys.subscription }) === subscriptionQuery;

    const reconcile = async () => {
      if (!currentSession() || inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        // A assinatura já traz a inadimplência avaliada pelo backend.
        const subscription = await accountService.getMySubscription();
        if (!currentSession()) return;
        const next = avaliarBloqueio({
          subscription,
          terminalSubscription: latest.current.terminalSubscription,
        });
        const stillWaiting = awaitingPayment(subscription, next.blockReason);
        if (!stillWaiting) {
          const refreshed = await latest.current.refreshProfile();
          if (!currentSession() || !refreshed) return;
        }

        // Só publica respostas desta sessão. Falhas preservam o último estado
        // confirmado; não desbloqueiam com uma assinatura ou perfil ainda obsoleto.
        queryClient.setQueryData(subscriptionQueryKeys.subscription, subscription);
        queryClient.setQueryData(subscriptionQueryKeys.subscriptionWarning, subscription);
        if (!stillWaiting) setPendingProfileUserId(null);
      } catch (error) {
        if (!cancelled) console.error('Erro ao verificar a confirmação do pagamento:', error);
      } finally {
        inFlight = false;
        schedule();
      }
    };

    const schedule = () => {
      if (!currentSession() || document.visibilityState === 'hidden') return;
      timer = window.setTimeout(() => {
        nextInterval = Math.min(nextInterval * BACKOFF_FACTOR, maxInterval);
        void reconcile();
      }, nextInterval);
    };
    const check = () => {
      window.clearTimeout(timer);
      nextInterval = interval;
      void reconcile();
    };
    schedule();
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [enabled, interval, maxInterval, queryClient, userId, cache, subscriptionQuery]);
}
